import { isScheduleOpen } from "@/utils/time.js";

function applyPriceAdjustments(items, priceAdjustType, priceAdjustAmount) {
  return items.map(item => {
    let adjustedPrice = item.originalPrice;
    if (priceAdjustType === 'percentUp') {
      adjustedPrice = item.originalPrice * (1 + priceAdjustAmount / 100);
    } else if (priceAdjustType === 'percentDecrease') {
      adjustedPrice = item.originalPrice * (1 - priceAdjustAmount / 100);
    }

    return {
      ...item,
      price: adjustedPrice,
      modifierSets: item.modifierSets.map(modifierSet => ({
        ...modifierSet,
        modifiers: modifierSet.modifiers.map(modifier => {
          let modAdjustedPrice = modifier.originalPrice;
          if (priceAdjustType === 'percentUp') {
            modAdjustedPrice = modifier.originalPrice * (1 + priceAdjustAmount / 100);
          } else if (priceAdjustType === 'percentDecrease') {
            modAdjustedPrice = modifier.originalPrice * (1 - priceAdjustAmount / 100);
          }
          return {
            ...modifier,
            price: modAdjustedPrice
          };
        })
      }))
    };
  });
}

export default {
  namespaced: true,
  state() {
    return {
      schedules: null,
      items: null,
      taxes: null,
      translations: null,
      priceAdjustType: null,
      priceAdjustAmount: null
    };
  },
  getters: {
    translationStrings: (state, getters, rootState, rootGetters) => {
      const language = rootGetters.currentLanguage;

      return state.translations?.[language] || {};
    },
    currentLanguage: (state, getters, rootState, rootGetters) => {
      return rootGetters.currentLanguage;
    },
    schedules: (state, getters, rootState) => {
      // Get the current order flow (order type) ID from the order module
      const currentOrderFlowId = rootState.order?.orderTypeId;
      const terminalConfig = rootState.terminal?.terminalDetails?.config;
      // 1. Per-order-flow override
      if (
        terminalConfig?.orderFlowSchedules &&
        currentOrderFlowId &&
        Array.isArray(terminalConfig.orderFlowSchedules[currentOrderFlowId])
      ) {
        return state.schedules?.filter(x => terminalConfig.orderFlowSchedules[currentOrderFlowId].includes(x.ref));
      }
      // 2. Global override (legacy)
      if (terminalConfig?.schedules && Array.isArray(terminalConfig.schedules) && terminalConfig.schedules.length > 0) {
        return state.schedules?.filter(x => terminalConfig.schedules.includes(x.ref));
      }
      // 3. Default: all schedules
      return state.schedules;
    },
    terminalSchedules: (state, getters, rootState) => {
      // Get terminal config and available order flows for this terminal
      const terminalConfig = rootState.terminal?.terminalDetails?.config;
      const availableOrderFlows = rootState.location?.config?.orderTypes?.filter(ot => {
        // Filter to only order flows available on this terminal
        if (terminalConfig?.orderFlows && Array.isArray(terminalConfig.orderFlows)) {
          return terminalConfig.orderFlows.includes(ot.id) && ot.active;
        }
        // If no terminal restriction, use all active order flows
        return ot.active;
      }) || [];

      // Collect all unique schedules available across these order flows
      const scheduleRefs = new Set();
      
      availableOrderFlows.forEach(orderFlow => {
        // Check for per-order-flow schedule override
        if (terminalConfig?.orderFlowSchedules && 
            Array.isArray(terminalConfig.orderFlowSchedules[orderFlow.id])) {
          // Use specific schedules for this order flow
          terminalConfig.orderFlowSchedules[orderFlow.id].forEach(ref => scheduleRefs.add(ref));
        } else if (terminalConfig?.schedules && Array.isArray(terminalConfig.schedules)) {
          // Use global terminal schedule override
          terminalConfig.schedules.forEach(ref => scheduleRefs.add(ref));
        } else {
          // Use all schedules (no restrictions)
          state.schedules?.forEach(schedule => scheduleRefs.add(schedule.ref));
        }
      });

      // Return the actual schedule objects
      return state.schedules?.filter(schedule => scheduleRefs.has(schedule.ref)) || [];
    },
    availableItemRefs: (state, getters, rootState, rootGetters) => {
      const ignoreSchedules = rootGetters['location/config'].ignoreMenuSchedules;
      const now = rootGetters.currentTime;
      const openSchedules = getters.schedules?.filter(schedule =>
        isScheduleOpen(schedule, now, ignoreSchedules)
      ) || [];

      const itemRefs = new Set();
      openSchedules.forEach(schedule => {
        schedule.departments.forEach(department => {
          department.items.forEach(item => {
            itemRefs.add(item.ref);
          });
        });
      });
      return itemRefs;
    },
    items: (state, getters) => {
      if (getters.currentLanguage === 'en') {
        return state.items;
      }

      const translationStrings = getters.translationStrings;
      const applyTranslations = (item, translations) => {
        const translatedItem = { ...item };

        if (translations) {
          translatedItem.name = translations[item.name] || item.name;
          translatedItem.description = translations[item.description] || item.description;
        }

        translatedItem.modifierSets = item.modifierSets.map(modifierSet => ({
          ...modifierSet,
          name: translations[modifierSet.name] || modifierSet.name,
          modifiers: modifierSet.modifiers.map(modifier => ({
            ...modifier,
            name: translations[modifier.name] || modifier.name,
          })),
        }));

        return translatedItem;
      };

      if (!state.items) return [];

      return state.items.map(item => applyTranslations(item, translationStrings));
    },
    departments: (state, getters, rootState, rootGetters) => {
      const ignoreSchedules = rootGetters['location/config'].ignoreMenuSchedules;
      if (ignoreSchedules) {
        return getters.schedules.flatMap(schedule => schedule.departments);
      }
      const translationStrings = getters.translationStrings;
      const now = rootGetters.currentTime; // Use reactive time from root store
      let openDepartments = getters.schedules?.filter(schedule =>
        isScheduleOpen(schedule, now, ignoreSchedules)
      ).flatMap(schedule => schedule.departments) || [];  

      const availableLastOrderedItems = rootGetters['customer/availableLastOrderedItems'];
      if (availableLastOrderedItems.length > 0) {
        const reorderDepartment = {
          ref: 'reorder',
          name: 'Order Again',
          items: availableLastOrderedItems.map(item => ({ ref: item.itemRef, lineModifierSets: item.lineModifierSets })),
        };
        openDepartments = [reorderDepartment, ...openDepartments];
      }

      return openDepartments.reduce((unique, department) => {
        const existingDepartment = unique.find(dept => dept.ref === department.ref);
        if (existingDepartment) {
          existingDepartment.items = [
            ...existingDepartment.items,
            ...department.items.filter(item => 
              !existingDepartment.items.some(existingItem => existingItem.ref === item.ref)
            )
          ];
        } else {
          unique.push({
            ...department,
            name: translationStrings[department.name] || department.name,
          });
        }
        return unique;
      }, []);
    },
  },
  mutations: {
    SET_MENU(state, menu) {
      state.schedules = menu.schedules;
      state.items = menu.items.map(item => ({
        ...item,
        originalPrice: item.price,
        modifierSets: item.modifierSets.map(modifierSet => ({
          ...modifierSet,
          modifiers: modifierSet.modifiers.map(modifier => ({
            ...modifier,
            originalPrice: modifier.price,
          }))
        }))
      }));
      state.items = applyPriceAdjustments(state.items, state.priceAdjustType, state.priceAdjustAmount);
      state.taxes = menu.taxes;
      state.translations = menu.translations;
    },
    SET_STOCK(state, stock) {
      const stockStatusMap = stock.reduce((map, item) => {
        map[item.itemRef] = item.status;
        return map;
      }, {});
      if (state && state.items) {
        state.items = state.items.map((item) => ({
          ...item,
          stockStatus: stockStatusMap[item.ref] || "IN_STOCK",
        }));
      }
    },
    SET_PRICE_RULE(state, { type, amount }) {
      state.priceAdjustType = type;
      state.priceAdjustAmount = amount;
      if (state.items) {
        state.items = applyPriceAdjustments(state.items, type, amount);
      }
    }
  },
  actions: {
    setPriceRule({ commit }, { type, amount }) {
      console.log('setPriceRule', type, amount);
      commit("SET_PRICE_RULE", { type, amount });
    },
  },
};
